import OSS from "ali-oss";
import {ossApi} from "@/api/system";

const STORAGE_KEY = "oss_sts_credentials";

let ossClient = null;

/**
 * OSS 配置
 */
const defaultConfig = {
  bucket: "utm-oss",
  region: "cn-guangzhou",
  endpoint: "oss-cn-guangzhou.aliyuncs.com"
};

/**
 * 获取 OSS 客户端实例
 */
export async function getOssClient(config = {}) {
  const finalConfig = { ...defaultConfig, ...config };

  // 检查是否有缓存的客户端且凭证有效
  if (ossClient) {
    const isValid = await checkCredentialsValid();
    if (isValid) {
      return ossClient;
    }
  }

  try {
    // 获取 STS 临时凭证
    const credentials = await fetchCredentials();

    // 验证凭证数据
    if (!credentials.accessKeyId || !credentials.accessKeySecret || !credentials.securityToken) {
      this.$message.error('STS 凭证数据不完整，缺少必要的字段');
    }

    // 创建 OSS 客户端
    ossClient = new OSS({
      region: finalConfig.region,
      endpoint: finalConfig.endpoint,
      accessKeyId: credentials.accessKeyId,
      accessKeySecret: credentials.accessKeySecret,
      stsToken: credentials.securityToken,
      bucket: finalConfig.bucket
    });

    console.log('OSS 客户端初始化成功');
    return ossClient;
  } catch (error) {
    console.error('初始化 OSS 客户端失败:', error);
    throw error;
  }
}

/**
 * 检查凭证是否有效
 */
async function checkCredentialsValid() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;

    const credentials = JSON.parse(stored);

    // 检查必需字段是否存在
    if (!credentials.accessKeyId || !credentials.accessKeySecret || !credentials.securityToken) {
      console.warn('缓存的凭证数据不完整，将重新获取');
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }

    const timestamp = credentials.timestamp;
    const now = Date.now();
    const elapsed = now - timestamp;

    // 凭证缓存时长小于 50 分钟才有效（STS 凭证有效期 1 小时，预留 10 分钟缓冲）
    return elapsed < 50 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * 获取 STS 临时凭证
 */
async function fetchCredentials() {
  const isValid = await checkCredentialsValid();
  if (isValid) {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      console.log('使用缓存的 STS 凭证');
      return JSON.parse(stored);
    }
  }

  // 从后端获取 STS 凭证
  try {
    const response = await ossApi.getSTSToken();
    console.log('STS 凭证接口响应:', response);

    // 检查响应数据
    if (!response || !response.result) {
      this.$message.error('接口返回数据为空');
      return Promise.reject();
    }

    const credentials = response.result;
    console.log("获取 STS 凭证成功", credentials);

    // 验证必需字段
    if (!credentials.accessKeyId) {
      this.$message.error('缺少 accessKeyId 字段');
      return Promise.reject();
    }
    if (!credentials.accessKeySecret) {
      this.$message.error('缺少 accessKeySecret 字段');
      return Promise.reject("缺少 accessKeySecret 字段");
    }
    if (!credentials.securityToken) {
      this.$message.error('缺少 securityToken 字段');
      return Promise.reject();
    }

    // 缓存凭证
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...credentials, timestamp: Date.now() })
    );

    return credentials;
  } catch (error) {
    console.error('获取 STS 凭证失败:', error);
    // 清除无效缓存
    localStorage.removeItem(STORAGE_KEY);
    this.$message.error(`获取 STS 凭证失败: ${error.message}`);
  }
}

export async function uploadFile(file, key, config = {}) {
  try {
    console.log('开始上传文件到 OSS:', file.name);

    const client = await getOssClient(config);
    // 生成唯一文件名：前缀/时间戳-原文件名
    const fullKey = `${key}/${Date.now()}-${file.name}`;
    console.log('OSS 文件路径:', fullKey);

    // 上传文件
    const result = await client.put(fullKey, file);

    console.log("OSS 上传结果", result);

    return {
      url: result.url,
      key: fullKey
    };
  } catch (error) {
    console.error('OSS 上传失败:', error);
    return  Promise.reject(error.message || '文件上传失败');
  }
}
